/**
 * Who an object of an app list is (L48, 28/10/2026). Domino finds an object
 * again by itself: the sheet says what an object is, the result's columns say
 * what they designate by a role, and every object keeps all its keys. Nobody
 * chooses « the » identity column. Mirrored in SQL by `app_identity_roles_r1`
 * and `app_row_keys_r1` (migration 20261028100000).
 */

export const APP_OBJECT_KINDS = ['company', 'person', 'posting', 'event', 'other'] as const;
export type AppObjectKind = typeof APP_OBJECT_KINDS[number];

/** How the sheet names each kind (« Chaque objet est … »). */
export const APP_OBJECT_KIND_LABELS: Record<AppObjectKind, string> = {
  company: 'une entreprise',
  person: 'une personne',
  posting: 'une offre ou une annonce',
  event: 'un rendez-vous ou un appel',
  other: 'autre chose',
};

/** Roles a result column may carry; the first column is always the element's name. */
export const APP_COLUMN_ROLES = ['company', 'domain', 'crm_id', 'url', 'email', 'event_id'] as const;
export type AppColumnRole = typeof APP_COLUMN_ROLES[number];

/** Roles of an object's keys: a column role, the element's name, or the CRM company Domino linked it to. */
export type AppKeyRole = AppColumnRole | 'name' | 'crm_company';

/**
 * The keys that identify an object of each kind, surest first; the name (with
 * the company for a person or a posting) is always the last resort. The CRM ID
 * is authoritative (Benoit, 28/10): two objects with different CRM IDs are
 * never the same. No SIREN: nobody works with it and it only exists in France.
 */
export const APP_IDENTITY_ROLES: Record<AppObjectKind, AppColumnRole[]> = {
  company: ['crm_id', 'domain'],
  person: ['crm_id', 'email', 'url'],
  posting: ['url'],
  event: ['event_id', 'url'],
  other: ['crm_id', 'url', 'email', 'event_id'],
};

/**
 * Domains shared by many companies: an address there, or a page on one of
 * these sites, never tells which company an element is.
 */
export const SHARED_DOMAINS = [
  'gmail.com', 'googlemail.com', 'outlook.com', 'outlook.fr', 'hotmail.com', 'hotmail.fr', 'live.com', 'live.fr',
  'msn.com', 'yahoo.com', 'yahoo.fr', 'icloud.com', 'me.com', 'aol.com', 'orange.fr', 'wanadoo.fr', 'free.fr',
  'sfr.fr', 'laposte.net', 'gmx.com', 'gmx.fr', 'protonmail.com', 'proton.me',
  'linkedin.com', 'welcometothejungle.com', 'indeed.com', 'fr.indeed.com', 'glassdoor.com', 'glassdoor.fr',
  'facebook.com', 'instagram.com', 'twitter.com', 'x.com', 'youtube.com', 'google.com',
] as const;
